import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useWisch } from './bewegung.js'

/**
 * Die Branchenschau – das Schaufenster der Branchenseite.
 *
 * Eine aufgeschlagene Karte in der Mitte – links der Text, rechts das Bild –
 * und seitlich schmale Streifen der Nachbarn, die nur ihr Bild zeigen. Wer
 * eine Vorschau anklickt, holt sie in die Mitte; von selbst rückt sie alle
 * sechs Sekunden weiter. Unter der Bühne steht je Eintrag ein Balken, der
 * aktive füllt sich, solange die Zeit läuft.
 *
 * Bewegt wird mit CSS-Übergängen auf transform, width, height und opacity.
 * Deshalb steht je Eintrag EIN Element, dessen Lage sich ändert: ein bei
 * jedem Wechsel neu gebautes Element hätte keinen Zustand, von dem aus ein
 * Übergang laufen könnte.
 */

export interface SchauStueck {
  id: string
  titel: string
  /** Kleine Marke über dem Titel, z. B. «24 Positionen». */
  marke: string
  /** Ein Satz. */
  text: string
  bild: string
}

type Stufe = 'mobil' | 'tablet' | 'desktop'

interface Mass {
  stufe: Stufe
  aktivB: number
  aktivH: number
  seiteB: number
  seiteH: number
  fernB: number
  fernH: number
  luecke: number
}

function klemme(min: number, wert: number, max: number): number {
  return Math.max(min, Math.min(max, wert))
}

/**
 * Die Masse der Bühne.
 *
 * Erst steht fest, was die Vorschauen brauchen, dann bekommt die grosse Karte
 * den Rest – nicht umgekehrt. Die Vorlage setzt für jede Stufe feste Pixel
 * (762, 560, 340); das geht gut, solange der Bildschirm genau so breit ist
 * wie erwartet, und bricht bei 468 Pixeln. Gerechnet passt es überall.
 */
function messen(breite: number): Mass {
  const stufe: Stufe = breite >= 1120 ? 'desktop' : breite >= 760 ? 'tablet' : 'mobil'
  const luecke = stufe === 'mobil' ? 8 : 14
  const seiteB = Math.round(klemme(26, breite * 0.075, 104))
  const fernB = stufe === 'desktop' ? Math.round(klemme(34, breite * 0.05, 74)) : 0
  const aussen = 2 * (luecke + seiteB) + (fernB ? 2 * (Math.round(luecke * 0.7) + fernB) : 0)
  const aktivB = Math.round(klemme(200, breite - aussen, 720))
  /*
    EIN Seitenverhältnis für alle Geräte.

    Der erste Entwurf machte die Karte auf dem Handy hoch (1,16) und auf dem
    Laptop breit (0,54) und stellte Bild und Text dort untereinander statt
    nebeneinander. Das waren zwei Darstellungen, nicht eine – und genau das
    soll es nicht sein. Jetzt liegt überall dasselbe Verhältnis an, und was
    sich zwischen Handy und Laptop ändert, ist allein die Grösse.
  */
  const aktivH = Math.round(klemme(150, aktivB * 0.62, 400))
  return {
    stufe,
    aktivB,
    aktivH,
    seiteB,
    seiteH: Math.round(aktivH * 0.74),
    fernB,
    fernH: Math.round(aktivH * 0.46),
    luecke
  }
}

interface Lage {
  x: number
  b: number
  h: number
  deckung: number
  ebene: number
  klickbar: boolean
}

function lageVon(v: number, m: Mass): Lage {
  const halb = m.aktivB / 2
  const s = Math.sign(v)
  const weit = Math.abs(v)

  if (weit === 0) {
    return { x: -halb, b: m.aktivB, h: m.aktivH, deckung: 1, ebene: 3, klickbar: false }
  }
  if (weit === 1) {
    const x = s < 0 ? -halb - m.luecke - m.seiteB : halb + m.luecke
    return { x, b: m.seiteB, h: m.seiteH, deckung: 1, ebene: 2, klickbar: true }
  }
  if (weit === 2 && m.fernB) {
    const sprung = m.luecke + m.seiteB + Math.round(m.luecke * 0.7)
    const x = s < 0 ? -halb - sprung - m.fernB : halb + sprung
    return { x, b: m.fernB, h: m.fernH, deckung: 1, ebene: 1, klickbar: true }
  }
  /* Weiter draussen: ausserhalb des Bildes geparkt und unsichtbar. Sie
     bleiben im Baum, damit der Übergang von dort hereinlaufen kann. */
  const weg = halb + m.luecke + m.seiteB + (m.fernB ? m.fernB + 40 : 0) + 80
  return {
    x: s < 0 ? -weg - (m.fernB || m.seiteB) : weg,
    b: m.fernB || m.seiteB,
    h: m.fernH || m.seiteH,
    deckung: 0,
    ebene: 0,
    klickbar: false
  }
}

const TAKT = 6000

export function Branchenschau({
  stuecke,
  aktiv,
  aufAktiv,
  selbstlauf = true
}: {
  stuecke: SchauStueck[]
  aktiv: number
  aufAktiv: (i: number) => void
  selbstlauf?: boolean
}): JSX.Element {
  const anzahl = stuecke.length
  const rahmen = useRef<HTMLDivElement>(null)
  const buehne = useRef<HTMLDivElement>(null)
  const [mass, setMass] = useState<Mass>(() => messen(1180))
  const [fortschritt, setFortschritt] = useState(0)
  const [halt, setHalt] = useState(false)
  const [ruhig, setRuhig] = useState(false)

  useEffect(() => {
    setRuhig(window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  }, [])

  useLayoutEffect(() => {
    const el = buehne.current
    if (!el) return
    const neu = (): void => setMass(messen(el.getBoundingClientRect().width))
    neu()
    const beobachter = new ResizeObserver(neu)
    beobachter.observe(el)
    return () => beobachter.disconnect()
  }, [])

  const hin = useCallback(
    (i: number) => {
      aufAktiv(((i % anzahl) + anzahl) % anzahl)
      setFortschritt(0)
    },
    [anzahl, aufAktiv]
  )

  /*
    Wischen: eine Karte je angefangenem Schwellenweg.

    Der Stand läuft über eine Referenz und nicht über `aktiv`. Ein langer
    Wisch löst mehrere Schritte im SELBEN Ereignis aus; `aktiv` kommt aber
    erst nach dem nächsten Rendern nach, und zwei Schritte hintereinander
    rechneten beide vom alten Wert aus – gemessen rückte ein Wisch über 120
    Pixel nur um eine Karte statt um zwei.
  */
  const stand = useRef(aktiv)
  useEffect(() => {
    stand.current = aktiv
  }, [aktiv])
  useWisch(
    buehne,
    useCallback(
      (richtung: 1 | -1) => {
        stand.current = ((stand.current + richtung) % anzahl + anzahl) % anzahl
        aufAktiv(stand.current)
        setFortschritt(0)
      },
      [anzahl, aufAktiv]
    )
  )

  /*
    Der Selbstlauf.

    Gemessen wird die vergangene Zeit und nicht die Zahl der Bilder: ein Tab
    im Hintergrund bekommt keine Bilder, und ein Zähler stünde beim
    Zurückkommen falsch. Angehalten wird, sobald jemand da ist.
  */
  useEffect(() => {
    if (!selbstlauf || halt || ruhig || anzahl < 2) return
    let id = 0
    let letzte = 0
    let gelaufen = 0
    const schlag = (t: number): void => {
      id = requestAnimationFrame(schlag)
      if (!letzte) {
        letzte = t
        return
      }
      gelaufen += Math.min(64, t - letzte)
      letzte = t
      if (gelaufen >= TAKT) {
        gelaufen = 0
        hin(aktiv + 1)
        return
      }
      setFortschritt(gelaufen / TAKT)
    }
    id = requestAnimationFrame(schlag)
    return () => cancelAnimationFrame(id)
  }, [selbstlauf, halt, ruhig, anzahl, aktiv, hin])

  return (
    <div
      ref={rahmen}
      className="rp-bs"
      role="group"
      aria-label="Gewerbe wählen"
      tabIndex={0}
      onMouseEnter={() => setHalt(true)}
      onMouseLeave={() => setHalt(false)}
      onFocus={() => setHalt(true)}
      onBlur={() => setHalt(false)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault()
          hin(aktiv - 1)
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault()
          hin(aktiv + 1)
        }
      }}
    >
      <div className="rp-bs-buehne" ref={buehne} style={{ height: `${mass.aktivH}px` }}>
        {stuecke.map((s, i) => {
          /* Der kürzere Weg um den Kranz. */
          let v = i - aktiv
          if (v > anzahl / 2) v -= anzahl
          if (v < -anzahl / 2) v += anzahl
          const l = lageVon(v, mass)
          const ist = v === 0
          return (
            <button
              key={s.id}
              type="button"
              className="rp-bs-karte"
              data-aktiv={ist ? 'ja' : undefined}
              aria-label={s.titel}
              aria-pressed={ist}
              tabIndex={-1}
              onClick={() => {
                if (!ist) hin(i)
              }}
              style={{
                transform: `translate(${l.x}px, ${-l.h / 2}px)`,
                width: `${l.b}px`,
                height: `${l.h}px`,
                opacity: l.deckung,
                zIndex: l.ebene,
                pointerEvents: l.klickbar ? 'auto' : 'none',
                cursor: l.klickbar ? 'pointer' : 'default'
              }}
            >
              {/* Die schmale Vorschau: nur das Bild, randlos in der Karte. */}
              <span className="rp-bs-voll" aria-hidden="true">
                {/* Ohne Bilddatei bleibt die Fläche dunkel, statt dass ein
                    kaputtes Bildsymbol erscheint. */}
                {s.bild && <img src={s.bild} alt="" draggable={false} />}
              </span>

              {/*
                Die aufgeschlagene Karte: links der Text, rechts das Bild.

                Sie hat IMMER die Masse der aktiven Karte, auch wenn die Karte
                gerade schmal ist – sie wird dann einfach abgeschnitten. Das
                ist der Kniff der Vorlage und der Grund, warum beim Wechsel
                nichts umbricht: der Text wird nicht in eine 44 Pixel breite
                Spalte gequetscht und wieder heraus, er liegt die ganze Zeit
                in seiner endgültigen Breite und schiebt sich nur ins Bild.
              */}
              <span
                className="rp-bs-offen"
                aria-hidden={!ist}
                style={{ width: `${mass.aktivB}px`, height: `${mass.aktivH}px` }}
              >
                <span className="rp-bs-spalte">
                  <small>{s.marke}</small>
                  <strong>{s.titel}</strong>
                  <span className="rp-bs-satz">{s.text}</span>
                </span>
                <span className="rp-bs-feld">
                  {s.bild && <img src={s.bild} alt="" draggable={false} />}
                </span>
              </span>
            </button>
          )
        })}

        <button
          type="button"
          className="rp-bs-pfeil"
          data-seite="links"
          aria-label="Vorheriges Gewerbe"
          onClick={() => hin(aktiv - 1)}
        >
          <Pfeil richtung="links" />
        </button>
        <button
          type="button"
          className="rp-bs-pfeil"
          data-seite="rechts"
          aria-label="Nächstes Gewerbe"
          onClick={() => hin(aktiv + 1)}
        >
          <Pfeil richtung="rechts" />
        </button>
      </div>

      {/*
        Die Leiste. Der aktive Balken ist breit und füllt sich, solange der
        Selbstlauf läuft – so ist zu sehen, dass gleich gewechselt wird, statt
        dass es einen überrascht.
      */}
      <div className="rp-bs-leiste">
        {stuecke.map((s, i) => (
          <button
            key={s.id}
            type="button"
            className="rp-bs-marke"
            data-aktiv={i === aktiv ? 'ja' : undefined}
            aria-label={s.titel}
            tabIndex={-1}
            onClick={() => hin(i)}
          >
            {i === aktiv && (
              <span
                className="rp-bs-fuellung"
                style={{ transform: `scaleX(${halt || ruhig ? 1 : fortschritt})` }}
              />
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

function Pfeil({ richtung }: { richtung: 'links' | 'rechts' }): JSX.Element {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {richtung === 'links' ? <path d="M15 18 9 12l6-6" /> : <path d="m9 18 6-6-6-6" />}
    </svg>
  )
}
